import { Layout } from "@/components/layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Search,
  X,
  Plus,
  Loader2,
  Trash2,
  Image as ImageIcon,
  ChevronLeft,
  MoreHorizontal,
  Heart,
  ExternalLink,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogHeader,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useState, useMemo } from "react";
import { useLocation, useRoute } from "wouter";
import Masonry from "react-masonry-css";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import type { Board, Ad } from "@shared/schema";

const masonryBreakpoints = {
  default: 3,
  1024: 3,
  768: 2,
  640: 1,
};

function BoardCard({
  board,
  adCount,
  coverImages,
  onClick,
  onDelete,
}: {
  board: Board;
  adCount: number;
  coverImages: string[];
  onClick: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      data-testid={`card-board-${board.id}`}
      className="group rounded-xl overflow-hidden cursor-pointer bg-white border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all"
      onClick={onClick}
    >
      <div className="aspect-[4/3] bg-gray-100 relative overflow-hidden">
        {coverImages.length > 0 ? (
          <div className={`grid ${coverImages.length === 1 ? "grid-cols-1" : "grid-cols-2"} gap-0.5 h-full`}>
            {coverImages.slice(0, 4).map((url, i) => (
              <img
                key={i}
                src={url}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <ImageIcon className="h-12 w-12 text-gray-300" />
          </div>
        )}
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="h-7 w-7 rounded-full bg-white/90 shadow-sm flex items-center justify-center hover:bg-white"
                onClick={(e) => e.stopPropagation()}
                data-testid={`button-board-menu-${board.id}`}
              >
                <MoreHorizontal className="h-4 w-4 text-gray-600" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="text-red-600 focus:text-red-600"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                data-testid={`button-delete-board-${board.id}`}
              >
                <Trash2 className="h-3.5 w-3.5 mr-2" />
                Delete Board
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="px-4 py-3">
        <h3 className="font-semibold text-gray-900 text-sm truncate" data-testid={`text-board-title-${board.id}`}>
          {board.title}
        </h3>
        <p className="text-xs text-gray-500 mt-0.5" data-testid={`text-board-count-${board.id}`}>
          {adCount} ad{adCount !== 1 ? "s" : ""}
        </p>
      </div>
    </div>
  );
}

function SavedAdCard({
  ad,
  onDelete,
  onFavorite,
}: {
  ad: Ad;
  onDelete: () => void;
  onFavorite: () => void;
}) {
  return (
    <div
      data-testid={`card-saved-ad-${ad.id}`}
      className="mb-3 group rounded-lg overflow-hidden bg-white shadow-sm border border-gray-100"
    >
      {ad.mediaUrl && (
        <div className="relative">
          <img
            src={ad.mediaUrl}
            alt={ad.pageName}
            className="w-full h-auto block"
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
          <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              className={`h-7 w-7 rounded-full shadow-sm flex items-center justify-center transition-colors ${
                ad.isFavorite
                  ? "bg-red-500 text-white"
                  : "bg-white/90 text-gray-600 hover:bg-white"
              }`}
              onClick={(e) => {
                e.stopPropagation();
                onFavorite();
              }}
              data-testid={`button-favorite-ad-${ad.id}`}
            >
              <Heart className={`h-3.5 w-3.5 ${ad.isFavorite ? "fill-current" : ""}`} />
            </button>
            <button
              className="h-7 w-7 rounded-full bg-white/90 shadow-sm flex items-center justify-center text-gray-600 hover:bg-white hover:text-red-500"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              data-testid={`button-delete-ad-${ad.id}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
      <div className="px-3 py-2">
        <p className="text-xs font-medium text-gray-900 truncate" data-testid={`text-ad-name-${ad.id}`}>
          {ad.pageName}
        </p>
        {ad.adCreativeBody && (
          <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2" data-testid={`text-ad-body-${ad.id}`}>
            {ad.adCreativeBody}
          </p>
        )}
        <div className="flex items-center gap-2 mt-1.5">
          {ad.platform && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-medium" data-testid={`text-ad-platform-${ad.id}`}>
              {ad.platform}
            </span>
          )}
          {ad.status && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-50 text-green-600 font-medium" data-testid={`text-ad-status-${ad.id}`}>
              {ad.status}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Boards() {
  const [, setLocation] = useLocation();
  const [, params] = useRoute("/boards/:id");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [newBoardTitle, setNewBoardTitle] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const boardId = params?.id ? Number(params.id) : null;

  const { data: boards = [], isLoading: boardsLoading } = useQuery<Board[]>({
    queryKey: ["/api/boards"],
  });

  const { data: currentBoard } = useQuery<Board>({
    queryKey: [`/api/boards/${boardId}`],
    enabled: !!boardId,
  });

  const { data: boardAds = [], isLoading: adsLoading } = useQuery<Ad[]>({
    queryKey: [`/api/boards/${boardId}/ads`],
    enabled: !!boardId,
  });

  const boardAdCounts = useQuery<Record<number, { count: number; images: string[] }>>({
    queryKey: ["/api/boards", "ad-counts"],
    queryFn: async () => {
      const counts: Record<number, { count: number; images: string[] }> = {};
      for (const board of boards) {
        const res = await fetch(`/api/boards/${board.id}/ads`);
        const ads: Ad[] = await res.json();
        counts[board.id] = {
          count: ads.length,
          images: ads.filter((a) => a.mediaUrl).map((a) => a.mediaUrl!).slice(0, 4),
        };
      }
      return counts;
    },
    enabled: boards.length > 0 && !boardId,
  });

  const createBoardMutation = useMutation({
    mutationFn: async (title: string) => {
      const res = await apiRequest("POST", "/api/boards", { title });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/boards"] });
      toast({ title: "Board created" });
      setCreateOpen(false);
      setNewBoardTitle("");
    },
    onError: (e: Error) => toast({ title: "Failed to create board", description: e.message, variant: "destructive" }),
  });

  const deleteBoardMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/boards/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/boards"] });
      toast({ title: "Board deleted" });
      if (boardId) setLocation("/boards");
    },
    onError: (e: Error) => toast({ title: "Failed to delete board", description: e.message, variant: "destructive" }),
  });

  const deleteAdMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/ads/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/boards/${boardId}/ads`] });
      queryClient.invalidateQueries({ queryKey: ["/api/boards", "ad-counts"] });
      toast({ title: "Ad removed from board" });
    },
  });

  const favoriteAdMutation = useMutation({
    mutationFn: async ({ id, isFavorite }: { id: number; isFavorite: boolean }) => {
      await apiRequest("PATCH", `/api/ads/${id}/favorite`, { isFavorite });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/boards/${boardId}/ads`] });
    },
  });

  const filteredBoards = useMemo(() => {
    if (!searchQuery.trim()) return boards;
    const q = searchQuery.toLowerCase();
    return boards.filter((b) => b.title.toLowerCase().includes(q));
  }, [boards, searchQuery]);

  if (boardId && currentBoard) {
    return (
      <Layout>
        <div className="flex flex-col h-full bg-gray-50">
          <div className="border-b border-gray-200 bg-white px-6 py-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setLocation("/boards")}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                data-testid="button-back-to-boards"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <div className="flex-1">
                <h1 className="text-lg font-bold text-gray-900" data-testid="text-board-detail-title">
                  {currentBoard.title}
                </h1>
                <p className="text-xs text-gray-500">
                  {boardAds.length} saved ad{boardAds.length !== 1 ? "s" : ""}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => deleteBoardMutation.mutate(currentBoard.id)}
                data-testid="button-delete-current-board"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Delete
              </Button>
            </div>
          </div>

          <div className="flex-1 overflow-auto px-6 py-6">
            {adsLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : boardAds.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                  <ImageIcon className="h-6 w-6 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">No ads saved yet</h3>
                <p className="text-sm text-gray-500 mt-1 max-w-md">
                  Save ads from the Ad Templates or Find Ads pages to build your collection.
                </p>
                <div className="flex gap-2 mt-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setLocation("/ad-templates")}
                    data-testid="button-go-templates"
                  >
                    Ad Templates
                  </Button>
                  <Button
                    size="sm"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    onClick={() => setLocation("/find-ads")}
                    data-testid="button-go-find-ads"
                  >
                    Find Ads
                  </Button>
                </div>
              </div>
            ) : (
              <Masonry
                breakpointCols={masonryBreakpoints}
                className="flex -ml-4 w-auto"
                columnClassName="pl-4 bg-clip-padding"
              >
                {boardAds.map((ad) => (
                  <SavedAdCard
                    key={ad.id}
                    ad={ad}
                    onDelete={() => deleteAdMutation.mutate(ad.id)}
                    onFavorite={() =>
                      favoriteAdMutation.mutate({ id: ad.id, isFavorite: !ad.isFavorite })
                    }
                  />
                ))}
              </Masonry>
            )}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-col h-full bg-gray-50">
        <div className="border-b border-gray-200 bg-white px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900" data-testid="text-boards-title">
                Boards
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Organize saved ads into collections
              </p>
            </div>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              size="sm"
              onClick={() => setCreateOpen(true)}
              data-testid="button-create-board"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              New Board
            </Button>
          </div>
          {boards.length > 3 && (
            <div className="relative mt-3 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                data-testid="input-search-boards"
                placeholder="Search boards..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9 border-gray-200 bg-gray-50 focus:bg-white"
              />
              {searchQuery && (
                <button
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  onClick={() => setSearchQuery("")}
                  data-testid="button-clear-board-search"
                >
                  <X className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-auto px-6 py-6">
          {boardsLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : boards.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                <ImageIcon className="h-6 w-6 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">No boards yet</h3>
              <p className="text-sm text-gray-500 mt-1">
                Create your first board to start organizing ads.
              </p>
              <Button
                className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white"
                size="sm"
                onClick={() => setCreateOpen(true)}
                data-testid="button-create-first-board"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                Create Board
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredBoards.map((board) => (
                <BoardCard
                  key={board.id}
                  board={board}
                  adCount={boardAdCounts.data?.[board.id]?.count ?? 0}
                  coverImages={boardAdCounts.data?.[board.id]?.images ?? []}
                  onClick={() => setLocation(`/boards/${board.id}`)}
                  onDelete={() => deleteBoardMutation.mutate(board.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Board</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">Board Name</label>
            <Input
              data-testid="input-board-name"
              placeholder="e.g. Skincare Inspiration, Competitor Ads..."
              value={newBoardTitle}
              onChange={(e) => setNewBoardTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newBoardTitle.trim()) {
                  createBoardMutation.mutate(newBoardTitle.trim());
                }
              }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} data-testid="button-cancel-create">
              Cancel
            </Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={() => createBoardMutation.mutate(newBoardTitle.trim())}
              disabled={!newBoardTitle.trim() || createBoardMutation.isPending}
              data-testid="button-confirm-create"
            >
              {createBoardMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
              ) : (
                <Plus className="h-4 w-4 mr-1.5" />
              )}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
